import { doneCount, isComplete, type Entry } from "./session.ts";

/**
 * A superset is done in turns: set 1 of the first movement, set 1 of the
 * second, back to the first for set 2. Its lines sit next to each other in
 * the session and share a `supersetGroup`.
 */

/**
 * Where the next set of this superset is, once the movement at `position` has
 * logged its own: the partner still owing this round, or the first movement
 * again for the next one. Null outside a superset, before the first set here,
 * or once every partner has done its sets.
 */
export function supersetTurn(entries: Entry[], position: number): number | null {
  const bounds = supersetAround(entries, position);
  if (bounds === null) return null;
  const { first, last } = bounds;

  const done = doneCount(entries[position]!);
  if (done === 0) return null;

  const next = position === last ? first : position + 1;
  const partner = entries[next]!;
  if (isComplete(partner)) return null;
  // Later in the round, the partner is due while it lags behind; back at the
  // start of the round, while it has not moved past this one.
  const due = next > position ? doneCount(partner) < done : doneCount(partner) <= done;
  return due ? next : null;
}

/** The superset around `position`, by its first and last positions; null outside one. */
function supersetAround(entries: Entry[], position: number): { first: number; last: number } | null {
  const group = entries[position]?.supersetGroup ?? null;
  if (group === null) return null;
  let first = position;
  while (entries[first - 1]?.supersetGroup === group) first -= 1;
  let last = position;
  while (entries[last + 1]?.supersetGroup === group) last += 1;
  return first === last ? null : { first, last };
}

/**
 * The other movements of the superset `position` belongs to, in session
 * order: said before the first set, so the pairing is not learnt from the
 * button that sends one away mid-round.
 */
export function supersetPartners(entries: Entry[], position: number): Entry[] {
  const bounds = supersetAround(entries, position);
  if (bounds === null) return [];
  return entries.slice(bounds.first, bounds.last + 1).filter((_, offset) => bounds.first + offset !== position);
}

/** Whether the line at `position` is done in turns with the one before it. */
export function pairedWithPrevious(entries: Entry[], position: number): boolean {
  const group = entries[position]?.supersetGroup ?? null;
  return group !== null && entries[position - 1]?.supersetGroup === group;
}
